import { useSearchParams } from "react-router-dom";
import { EActiveTab } from "../../widgets/Interface/Interface";

export enum EViewType {
  ActiveTab = "activeTab",
  Templates = "templates",
  History = "history",
}

interface IActiveTabView {
  type: EViewType.ActiveTab;
  tab: EActiveTab;
  list_id: string;
}

interface ITemplateView {
  type: EViewType.Templates;
  template_id?: string;
  list_id: string;
}

interface IHistoryView {
  type: EViewType.History;
  history_id?: string;
  list_id: string;
}

export type TCurrentView = IActiveTabView | ITemplateView | IHistoryView;

export interface IUseURLRouting {
  currentView: TCurrentView;
  activeTab: EActiveTab;
  itemID: string;
  templateID: string | null;
  historyID: string | null;
  setTab: (tab: EActiveTab) => void;
  setItem: (id: string) => void;
  setTemplate: (id?: string) => void;
  setHistory: (id?: string) => void;
  resetTemplate: () => void;
  resetHistory: () => void;
}

export const useURLRouting = (): IUseURLRouting => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab =
    (searchParams.get("activeTab") as EActiveTab) || EActiveTab.Environment;
  // Sanitize list_id: accept digits, underscores, hashes, and alphanumeric (like "123_456_789#101_abc123")
  const rawListId = searchParams.get("list_id");
  const itemID = rawListId && /^[\w#]+$/.test(rawListId) ? rawListId : "";

  const templateID = searchParams.get("template_id");
  const historyID = searchParams.get("history_id");

  const hasHistory = searchParams.has("history");
  const hasTemplates = searchParams.has("templates");

  const setTab = (tab: EActiveTab) => {
    searchParams.set("activeTab", tab);
    searchParams.delete("list_id");
    setSearchParams(searchParams);
  };

  const setItem = (id: string) => {
    if (id) {
      searchParams.set("activeTab", activeTab);
      searchParams.set("list_id", id);
    } else {
      searchParams.set("activeTab", activeTab);
      searchParams.delete("list_id");
    }
    setSearchParams(searchParams);
  };

  const setTemplate = (id?: string) => {
    searchParams.delete("history");
    if (id) {
      searchParams.set("templates", "");
      searchParams.set("template_id", id);
    } else {
      searchParams.set("templates", "");
      searchParams.delete("template_id");
    }
    setSearchParams(searchParams);
  };

  const setHistory = (id?: string) => {
    searchParams.delete("templates");
    if (id) {
      searchParams.set("history", "");
      searchParams.set("history_id", id);
    } else {
      searchParams.set("history", "");
      searchParams.delete("history_id");
    }
    setSearchParams(searchParams);
  };

  const resetTemplate = () => {
    searchParams.delete("templates");
    searchParams.delete("template_id");
    searchParams.delete("foldersPage");
    searchParams.delete("templatesPage");
    setSearchParams(searchParams);
  };

  const resetHistory = () => {
    searchParams.delete("history");
    searchParams.delete("history_id");
    searchParams.delete("historyPage");
    setSearchParams(searchParams);
  };

  const currentView: TCurrentView = hasHistory
    ? {
        type: EViewType.History,
        history_id: historyID || undefined,
        list_id: itemID,
      }
    : hasTemplates
      ? {
          type: EViewType.Templates,
          template_id: templateID || undefined,
          list_id: itemID,
        }
      : { type: EViewType.ActiveTab, tab: activeTab, list_id: itemID };

  return {
    currentView,
    activeTab,
    itemID,
    templateID,
    historyID,
    setTab,
    setItem,
    setTemplate,
    setHistory,
    resetTemplate,
    resetHistory,
  };
};
